import React, { useState } from "react";
import styled, { css, keyframes } from "styled-components";
import { RippleEffectShapeEnum } from "./RippleEffectShapeEnum";

interface RippleEffectOuterProps {
  $shape: RippleEffectShapeEnum;
  $width?: string;
}

const RippleEffectOuter = styled.div<RippleEffectOuterProps>`
  overflow: hidden;
  position: sticky;

  ${(props) => {
    if (props.$width) {
      return css`
        width: ${props.$width};
      `;
    }
  }};

  ${(props) => {
    if (props.$shape === RippleEffectShapeEnum.CIRCLE) {
      return css`
        border-radius: 100%;
      `;
    } else if (props.$shape === RippleEffectShapeEnum.BUTTON) {
      return css`
        border-start-start-radius: 9999px;
        border-start-end-radius: 9999px;
        border-end-start-radius: 9999px;
        border-end-end-radius: 9999px;
      `;
    } else if (props.$shape === RippleEffectShapeEnum.RADIUS_16) {
      return css`
        border-radius: 16px;
      `;
    }
  }};
`;

interface RippleEffectInnerProps {
  $diameter: number;
  $left: number;
  $top: number;
  $animationLength: number;
}

const rippleAnimation = keyframes`
  to {
    transform: scale(4);
    opacity: 0;
  }
`;

const RippleEffectInner = styled.span<RippleEffectInnerProps>`
  position: absolute;
  border-radius: 50%;
  animation: ${rippleAnimation} ${(props) => props.$animationLength}ms linear;
  background-color: rgba(255, 255, 255);
  opacity: 0.3;
  transform: scale(0);
  width: ${(props) => props.$diameter}px;
  height: ${(props) => props.$diameter}px;
  left: ${(props) => props.$left}px;
  top: ${(props) => props.$top}px;
  filter: blur(6px);
`;

interface ClonedChildProps {
  children?: React.ReactNode;
}

interface RippleEffectProps {
  children: React.ReactNode;
  shape: RippleEffectShapeEnum;
  width?: string;
}

const RippleEffect: React.FC<RippleEffectProps> = ({ children, shape, width }) => {
  const animationLength = 300;

  const [isClicked, setClicked] = useState(false);
  const [rippleEffectInnerProps, setRippleEffectInnerProps] = useState<RippleEffectInnerProps>();

  const handleButtonClick = (event: React.MouseEvent<HTMLDivElement>) => {
    handleButtonPress(event.currentTarget, event.clientX, event.clientY);
  };

  const handleButtonTouch = (event: React.TouchEvent<HTMLDivElement>) => {
    handleButtonPress(event.currentTarget, event.touches[0].clientX, event.touches[0].clientY);
  };

  const handleButtonPress = (button: HTMLDivElement, x: number, y: number) => {
    const diameter = Math.max(button.clientWidth, button.clientHeight);
    const radius = diameter / 2;

    const rect = button.getBoundingClientRect();

    setRippleEffectInnerProps({
      $diameter: diameter,
      $left: x - rect.left - radius,
      $top: y - rect.top - radius,
      $animationLength: animationLength,
    });

    setClicked(true);

    setTimeout(() => {
      setClicked(false);
    }, animationLength);
  };

  return (
    <RippleEffectOuter
      onMouseDown={handleButtonClick}
      onTouchStart={handleButtonTouch}
      $shape={shape}
      $width={width}
    >
      {React.isValidElement(children) &&
        React.cloneElement(children as React.ReactElement<ClonedChildProps>, {
          ...(children.props as ClonedChildProps),
          children: [
            ...React.Children.toArray((children.props as ClonedChildProps).children),
            isClicked && (
              <RippleEffectInner
                key="RippleEffectInner"
                $diameter={rippleEffectInnerProps?.$diameter ?? 0}
                $left={rippleEffectInnerProps?.$left ?? 0}
                $top={rippleEffectInnerProps?.$top ?? 0}
                $animationLength={animationLength}
              />
            ),
          ],
        })}
    </RippleEffectOuter>
  );
};

export default RippleEffect;
